import { ReactNode, useState } from 'react';
import Image from 'next/image';
import classNames from 'classnames';
import { Check, X } from 'lucide-react';
import { CartLineT, LineItemType } from '@lambda/apis/src/cart/types';
import { WarrantyT } from '@lambda/apis/src/warranty/types';
import { ProductCategoryWarranty } from '@lambda/apis/src/category/types';
import { RC_DURATION, ReebeloStoreT } from '@lambda/reebelo';
import { handleFreeReturn } from '@lambda/commons/helpers';
import { useCartContext } from '@/utils/hooks/cart/useCartContext';
import { trackReebeloCareAdded } from '@/lib/analytics/trackProduct';
import { formatWarrantyInfo } from '@/lib/warranty';
import { priceFormatSuperscript } from '@/lib/price';
import { getPlanMonthlyPrice, getPlanMonths } from '@/lib/reebelocare/plan';
import ShieldIcon from '@/public/icons/reebelocare/cart.svg';
import settings, { priceFormater } from '@/settings';
import i18n from '@/settings/i18n';
import { INFO_DATA, InfoFooter } from '../RightSection/ReebeloCarePanel';
import ReebeloCareModal from '../RightSection/ReebeloCareModal';
import { buildReebeloCareLine } from '../helpers';
import { splitProductTitle } from '../../product/sections/main/right/product/helpers';
import CompareCoverageModal from './CompareCoverageModal';

const t = i18n.init('cart');

const freeReturnDays = handleFreeReturn(
  settings.store,
  settings.free_return_days,
);

const Benefit = ({
  included = true,
  children,
}: {
  included?: boolean;
  children: ReactNode;
}) => (
  <li
    className={classNames(
      'flex items-start gap-1.5 text-xs',
      !included && 'text-gray-700/40',
    )}
  >
    {included ? (
      <Check className="mt-px h-3 w-3 shrink-0 text-[#128452]" />
    ) : (
      <X className="mt-px h-3 w-3 shrink-0" />
    )}
    <span>{children}</span>
  </li>
);

/**
 * The plan's name per store: it names a choice, and then the plan in the
 * cart, so never an action like the old cart toggle's "Add …".
 */
const PLAN_TITLES: Partial<Record<ReebeloStoreT, string>> & {
  default: string;
} = {
  'reebelo-au': '24-Month ReebeloCare Plan',
  default: '24-Month Device Protection',
};

const OptionCard = ({
  selected,
  disabled,
  onSelect,
  badge,
  title,
  price,
  children,
  className,
}: {
  selected: boolean;
  disabled: boolean;
  onSelect: () => void;
  badge?: string;
  title: string;
  price: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  // The whole card selects it. Clicks on its own links and buttons (T&Cs,
  // Plan Summary, Compare coverage) and on the radio's label are left to
  // them; the radio stays the keyboard control.
  <div
    onClick={(event) => {
      const target = event.target as HTMLElement;

      if (
        selected ||
        disabled ||
        target.closest('a, button, input, label, [role="button"]')
      ) {
        return;
      }

      onSelect();
    }}
    className={classNames(
      'relative flex flex-col rounded-xl border p-4 text-[13px] leading-tight transition-colors duration-150',
      // Selected: a 1.5px border, the extra half pixel an inset ring so nothing shifts.
      selected
        ? 'border-gray-700 shadow-[inset_0_0_0_0.5px_theme(colors.gray.700)]'
        : 'border-[#E6E7EB]',
      !selected && !disabled && 'cursor-pointer hover:border-gray-700/50',
      !selected && disabled && 'cursor-wait',
      className,
    )}
  >
    {badge && (
      <span className="absolute -top-2.5 right-4 rounded bg-[#D6FFE2] px-2 py-0.5 text-[11px] font-medium text-[#0ca764]">
        {badge}
      </span>
    )}
    <label
      className={classNames(
        'flex items-start justify-between gap-3',
        disabled ? 'cursor-wait' : 'cursor-pointer',
      )}
    >
      <span className="flex items-start gap-2">
        <input
          type="radio"
          checked={selected}
          disabled={disabled}
          onChange={onSelect}
          className="h-4 w-4 shrink-0 accent-gray-700"
        />
        <span className="text-[13px] font-medium">{title}</span>
      </span>
      <span className="shrink-0 text-right">{price}</span>
    </label>
    <div className="mt-3 flex flex-1 flex-col justify-between gap-3">
      {children}
    </div>
  </div>
);

/**
 * ReebeloCare for a device line in the purchase flow, in place of "Frequently
 * bought together":
 * - a compact row with Remove, when the plan was already in the cart as this
 *   card first rendered (added from the PDP, or chosen before going to
 *   checkout and coming back);
 * - otherwise the choice between the plan and the standard warranty, which
 *   adds or removes the plan's cart line straight away.
 *
 * `lines` are the device's lines (one per seller). Each gets the plan for its
 * own price, as sellers can sit in different price bands; a line with no plan
 * for its price is left uncovered.
 */
const LineItemReebeloCare = ({
  lines,
  plansByLine,
  categoryWarranty,
}: {
  lines: CartLineT[];
  /** The plan MsWarranty offers for each line's own price, by line id. */
  plansByLine: Record<string, WarrantyT>;
  categoryWarranty?: ProductCategoryWarranty;
}) => {
  const { cartLines, cartLoading, addLinesToCart, removeLinesFromCart } =
    useCartContext();
  const [isUpdating, setIsUpdating] = useState(false);
  const [showPlanSummary, setShowPlanSummary] = useState(false);
  const [showCompare, setShowCompare] = useState(false);

  const deviceLineIds = new Set(lines.map((line) => line.id));
  const planLines = cartLines.filter(
    (line) =>
      line.type === LineItemType.WARRANTY &&
      line.reference?.typeId != null &&
      deviceLineIds.has(line.reference.typeId),
  );
  const hasPlan = planLines.length > 0;

  // Fully covered: every seller line that has a plan for its price carries one
  // (a line with none for its price can't be covered, so it doesn't count).
  // Partly covered keeps the choice on screen with neither option selected,
  // so the missing plans can still be added.
  const isFullyCovered =
    hasPlan &&
    lines.every(
      (line) =>
        !plansByLine[line.id] ||
        planLines.some((planLine) => planLine.reference?.typeId === line.id),
    );

  // The device's plans in full: every line that has one, one per unit (a plan
  // line takes its device line's quantity). What the card's "total" quotes, as
  // choosing the plan covers all of them.
  const planTotal = lines.reduce(
    (sum, line) => sum + (plansByLine[line.id]?.price ?? 0) * line.quantity,
    0,
  );

  // What choosing the plan adds to the cart: the uncovered lines' plans only.
  const addTotal = lines.reduce((sum, line) => {
    const plan = plansByLine[line.id];
    const covered = planLines.some(
      (planLine) => planLine.reference?.typeId === line.id,
    );

    return plan && !covered ? sum + plan.price * line.quantity : sum;
  }, 0);

  // Decided once, as the card first renders: a plan chosen here keeps the
  // choice on screen (so it can be switched back), and removing a plan from
  // the compact row brings the choice back. Compact only while fully covered.
  const [isCompact, setIsCompact] = useState(isFullyCovered);

  // The plan the card shows: the first line's that has one.
  const warranty = lines
    .map((line) => plansByLine[line.id])
    .find((plan): plan is WarrantyT => plan != null);

  const disabled = isUpdating || cartLoading;
  const title = t(PLAN_TITLES[settings.store] ?? PLAN_TITLES.default);

  const addPlan = async () => {
    if (!warranty) {
      return;
    }

    setIsUpdating(true);

    try {
      // Every device line still without a plan, in one request.
      const toCover = lines.flatMap((line) => {
        const plan = plansByLine[line.id];
        const covered = planLines.some(
          (planLine) => planLine.reference?.typeId === line.id,
        );

        return plan && !covered ? [{ line, plan }] : [];
      });

      const updatedCart = await addLinesToCart(
        toCover.map(({ line, plan }) => buildReebeloCareLine(line, plan)),
      );

      if (updatedCart) {
        toCover.forEach(({ line, plan }) =>
          trackReebeloCareAdded({
            entry_point: 'Cart',
            reebelocare_id: plan.id,
            device_psku: line.psku ?? '',
            device_vendor: line.vendor,
            device_sku: line.sku,
            duration: plan.duration.toString() as RC_DURATION,
          }),
        );
      }
    } finally {
      setIsUpdating(false);
    }
  };

  // Tracked as ReebeloCare Removed by the cart itself.
  const removePlan = async () => {
    if (planLines.length === 0) {
      return;
    }

    setIsUpdating(true);

    try {
      await removeLinesFromCart(planLines);
    } finally {
      setIsUpdating(false);
    }
  };

  // Also compact whenever there's a plan in the cart but none to offer (still
  // loading, or no longer offered), so it can always be removed.
  if ((isCompact && isFullyCovered) || (hasPlan && !warranty)) {
    const total = planLines.reduce(
      (sum, line) => sum + (line.price * line.quantity) / 100,
      0,
    );

    return (
      <div className="flex items-center justify-between gap-3 border-t border-[#F2F2F2] px-4 py-3 text-[13px]">
        <span className="flex min-w-0 items-center gap-2 font-medium">
          <Image
            src={ShieldIcon}
            alt=""
            width={16}
            height={16}
            className="h-4 w-4 shrink-0"
          />
          <span className="truncate">{title}</span>
        </span>
        <span className="flex shrink-0 items-center gap-4">
          <button
            type="button"
            disabled={disabled}
            onClick={async () => {
              await removePlan();
              setIsCompact(false);
            }}
            className="text-gray-700/60 hover:text-gray-700 hover:underline disabled:cursor-wait"
          >
            {t`Remove`}
          </button>
          <span className="font-bold" translate="no">
            {priceFormatSuperscript(priceFormater.long(total))}
          </span>
        </span>
      </div>
    );
  }

  // Nothing to offer: no plan for this device (and none in the cart).
  if (!warranty) {
    return null;
  }

  // Per-month price and duration only for plans sold in months (see plan.ts).
  const planMonths = getPlanMonths(warranty);
  const monthlyPrice = getPlanMonthlyPrice(warranty);
  const coverage = INFO_DATA[settings.store] ?? INFO_DATA.default;

  // The included warranty, the way formatWarrantyInfo resolves it. Months get
  // their own "12-Month Warranty" label; any other unit reads "1 Year
  // Warranty" through the generic one.
  const hasCategoryWarranty = Boolean(
    categoryWarranty?.eligible &&
      categoryWarranty.warrantyLength &&
      categoryWarranty.warrantyUnit,
  );
  let standardMonths: number | null = settings.warranty_month;

  if (hasCategoryWarranty) {
    standardMonths =
      categoryWarranty?.warrantyUnit?.toLowerCase() === 'month'
        ? categoryWarranty.warrantyLength ?? null
        : null;
  }

  const standardWarrantyLabel =
    standardMonths != null
      ? t`${{ count: String(standardMonths) }}-Month Warranty`
      : t`${{ duration: formatWarrantyInfo(categoryWarranty) }} Warranty`;

  return (
    <div className="border-t border-[#F2F2F2] px-4 py-4">
      {/* One line, a size down on phones so it fits from 320px. */}
      <h3 className="mb-4 flex items-center gap-1 whitespace-nowrap text-[10px] uppercase text-[#4A5565] xxs:text-[11px] md:gap-1.5 md:text-xs md:tracking-wide">
        {t`Protect your device with`}
        <Image
          src={ShieldIcon}
          alt=""
          width={16}
          height={16}
          className="h-4 w-4"
        />
        <span className="text-xs font-bold normal-case tracking-normal text-gray-700 xxs:text-[13px] md:text-sm">
          Reebelo<span className="text-[#128452]">Care</span>
        </span>
      </h3>
      <div className="grid gap-4 md:grid-cols-2">
        <OptionCard
          selected={isFullyCovered}
          disabled={disabled}
          onSelect={addPlan}
          badge={t`Recommended`}
          title={title}
          price={
            <>
              {monthlyPrice != null && (
                <span className="font-bold" translate="no">
                  {priceFormatSuperscript(
                    priceFormater.long(monthlyPrice),
                    t`/mo`,
                  )}
                </span>
              )}
              <span className="block text-xs text-gray-700/60" translate="no">
                {t`${{ price: priceFormater.short(planTotal) }} total`}
              </span>
            </>
          }
        >
          <ul className="flex flex-col gap-1">
            {planMonths != null && (
              <Benefit>
                {t`${{ months: String(planMonths) }}-Month Protection`}
              </Benefit>
            )}
            <Benefit>
              {t`${{ days: String(freeReturnDays) }}-Day Free Returns`}
            </Benefit>
            {coverage.map((item) => (
              <Benefit key={item}>{t(item)}</Benefit>
            ))}
          </ul>
          <InfoFooter
            isMobile={false}
            neutralLinkHover
            italic={false}
            onShowWarrantyDetails={() => setShowPlanSummary(true)}
          />
        </OptionCard>
        <OptionCard
          selected={!hasPlan}
          disabled={disabled}
          onSelect={removePlan}
          // Desktop only: Standard on the left so ReebeloCare reads as the
          // upgrade. Mobile keeps ReebeloCare on top.
          className="md:order-first"
          title={t`Standard Warranty`}
          price={<span className="font-bold">{t`Free`}</span>}
        >
          <ul className="flex flex-col gap-1">
            <Benefit>{standardWarrantyLabel}</Benefit>
            <Benefit>
              {t`${{ days: String(freeReturnDays) }}-Day Free Returns`}
            </Benefit>
            <Benefit included={false}>
              {t`Drops, spills & cracked screens included`}
            </Benefit>
          </ul>
          <button
            type="button"
            onClick={() => setShowCompare(true)}
            className="w-fit text-[11px] text-gray-700/60 underline hover:text-gray-700"
          >
            {t`Compare coverage`}
          </button>
        </OptionCard>
      </div>
      <ReebeloCareModal
        showModal={showPlanSummary}
        setShowModal={setShowPlanSummary}
      />
      <CompareCoverageModal
        open={showCompare}
        onClose={() => setShowCompare(false)}
        productTitle={splitProductTitle(lines[0].title).primaryTitle}
        plan={warranty}
        standardWarranty={formatWarrantyInfo(categoryWarranty)}
        isPlanAdded={isFullyCovered}
        addTotal={addTotal}
        disabled={disabled}
        onAddPlan={addPlan}
        onKeepStandard={removePlan}
      />
    </div>
  );
};

export default LineItemReebeloCare;
